// Service to get all collections for cart products using Storefront API
// Uses elegant cart GraphQL query with nodes instead of individual product queries
// This replaces the backend collection queries with a single efficient frontend call

import createCandyRackClient from '../graphql/client';
import { logError, logInfo } from '../shared/utils/logging';
import { REGISTERED_APPS } from '../shared/types/Apps';
import { getIdFromGid } from '../shared/api/utils';

class CartCollectionsService {
  private storefrontAccessToken: string;
  private runningAppName: string;
  private readonly MAX_RETRIES = 2;
  private readonly RETRY_DELAY = 1000; // 1 second

  constructor(
    storefrontAccessToken: string,
    runningAppName: string = 'candy-rack',
  ) {
    this.storefrontAccessToken = storefrontAccessToken;
    this.runningAppName = runningAppName;
  }

  /**
   * Get all unique collection IDs from cart products
   * This replaces the deprecated backend collection query approach
   */
  async getCartCollectionIds(cartToken: string): Promise<number[]> {
    const collectionData = await this.getCartCollectionData(cartToken);
    return collectionData.collectionIds;
  }

  /**
   * Get comprehensive collection data including products in collections
   * This provides both collection IDs and which products are in each collection
   */
  async getCartCollectionData(cartToken: string): Promise<{
    collectionIds: number[];
    productsInCollections: Record<number, number[]>;
  }> {
    if (!cartToken) {
      logInfo(
        'No cart token provided to CartCollectionsService',
        this.runningAppName as REGISTERED_APPS,
      );
      return { collectionIds: [], productsInCollections: {} };
    }

    if (!this.storefrontAccessToken) {
      logInfo(
        'No storefront access token available for CartCollectionsService',
        this.runningAppName as REGISTERED_APPS,
      );
      return { collectionIds: [], productsInCollections: {} };
    }

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        const data = await this.fetchCollectionDataWithRetry(cartToken);

        if (data.collectionIds.length > 0) {
          logInfo(
            `Successfully fetched ${
              data.collectionIds.length
            } collections with ${
              Object.keys(data.productsInCollections).length
            } collection-product mappings from Storefront API`,
            this.runningAppName as REGISTERED_APPS,
          );
        } else {
          logInfo(
            'No collections found for cart products',
            this.runningAppName as REGISTERED_APPS,
          );
        }

        return data;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        if (attempt < this.MAX_RETRIES) {
          logInfo(
            `Retrying cart collections fetch (attempt ${attempt}/${this.MAX_RETRIES}): ${lastError.message}`,
            this.runningAppName as REGISTERED_APPS,
          );
          await this.delay(this.RETRY_DELAY);
        }
      }
    }

    // All retries failed
    logError(
      `Failed to fetch cart collections after ${this.MAX_RETRIES} attempts: ${lastError?.message}`,
      this.runningAppName as REGISTERED_APPS,
    );

    return { collectionIds: [], productsInCollections: {} };
  }

  private async fetchCollectionDataWithRetry(cartToken: string): Promise<{
    collectionIds: number[];
    productsInCollections: Record<number, number[]>;
  }> {
    const storefrontApiClient = createCandyRackClient(
      this.storefrontAccessToken,
    );

    // Storefront client returns null if no token was provided
    if (!storefrontApiClient) {
      throw new Error('Failed to create Storefront API client');
    }

    const query = this.buildCartCollectionsQuery();
    const cartGlobalId = `gid://shopify/Cart/${cartToken}`;

    const {
      data,
      errors,
      // @ts-ignore - the types from the Shopify Storefront API client are not complete
    } = await storefrontApiClient.request(query, {
      variables: { cartId: cartGlobalId },
    });

    // Handle GraphQL errors
    if (errors) {
      const errorMessages = Array.isArray(errors)
        ? errors.map((e: any) => e.message || String(e)).join(', ')
        : String(errors);
      throw new Error(`GraphQL errors: ${errorMessages}`);
    }

    // Check for empty response
    if (!data) {
      throw new Error('Empty response from Storefront API');
    }

    // Handle empty cart
    if (!data.cart) {
      logInfo(
        'Cart not found in Storefront API response (may be empty or invalid token)',
        this.runningAppName as REGISTERED_APPS,
      );
      return { collectionIds: [], productsInCollections: {} };
    }

    return this.parseCartCollectionData(data);
  }

  private buildCartCollectionsQuery(): string {
    // Build a comprehensive query that gets collections and their products
    return `
      query GetCartProductCollections($cartId: ID!) {
        cart(id: $cartId) {
          lines(first: 250) {
            nodes {
              merchandise {
                ... on ProductVariant {
                  product {
                    id
                    collections(first: 250) {
                      nodes {
                        id
                        products(first: 250) {
                          nodes {
                            id
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  private parseCartCollectionData(data: any): {
    collectionIds: number[];
    productsInCollections: Record<number, number[]>;
  } {
    const collectionIds = new Set<number>();
    const productsInCollections: Record<number, number[]> = {};

    if (!data.cart?.lines?.nodes) {
      return { collectionIds: [], productsInCollections: {} };
    }

    let totalProducts = 0;
    let productsWithCollections = 0;

    data.cart.lines.nodes.forEach((line: any) => {
      const product = line.merchandise?.product;
      if (!product) return;

      totalProducts++;
      const productId = getIdFromGid(product.id);

      const collections = product.collections?.nodes || [];
      if (collections.length > 0) {
        productsWithCollections++;
      }

      collections.forEach((collection: any) => {
        if (collection?.id) {
          const collectionId = getIdFromGid(collection.id);
          if (collectionId > 0) {
            collectionIds.add(collectionId);

            // Parse products in this collection
            if (!productsInCollections[collectionId]) {
              productsInCollections[collectionId] = [];
            }

            const productsInCollection = collection.products?.nodes || [];
            productsInCollection.forEach((collectionProduct: any) => {
              if (collectionProduct?.id) {
                const collectionProductId = getIdFromGid(collectionProduct.id);
                if (
                  collectionProductId > 0 &&
                  !productsInCollections[collectionId].includes(
                    collectionProductId,
                  )
                ) {
                  productsInCollections[collectionId].push(collectionProductId);
                }
              }
            });
          }
        }
      });
    });

    logInfo(
      `Parsed collections: ${
        collectionIds.size
      } unique collections from ${productsWithCollections}/${totalProducts} products, with ${
        Object.keys(productsInCollections).length
      } collection-product mappings`,
      this.runningAppName as REGISTERED_APPS,
    );

    return {
      collectionIds: Array.from(collectionIds),
      productsInCollections,
    };
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Check if Storefront API is available and properly configured
   */
  isAvailable(): boolean {
    const available = Boolean(this.storefrontAccessToken);

    if (!available) {
      logInfo(
        'CartCollectionsService is not available - no storefront access token',
        this.runningAppName as REGISTERED_APPS,
      );
    }

    return available;
  }
}

export default CartCollectionsService;
